import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ButtonProps } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

type CallToActionVariant = "aurora";

type Palette = {
  headingClass: string;
  descriptionClass: string;
  supportingCardClass: string;
  supportingBodyClass: string;
  supportingMetaClass: string;
  buttonVariant: ButtonProps["variant"];
  buttonClassName: string;
};

type VariantConfig = {
  sectionClass: string;
  panelClass: string;
  accentClass: string;
  eyebrowIcon: ReactNode;
  palette: Palette;
  decorative?: ReactNode;
};

type CallToActionProps = {
  variant?: CallToActionVariant;
  onNavigate?: () => void;
};

const variantConfigs: Record<CallToActionVariant, VariantConfig> = {
  aurora: {
    sectionClass:
      "bg-linear-to-br from-slate-100 via-white to-slate-200 text-slate-900 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 dark:text-white",
    panelClass:
      "border border-slate-200/70 bg-white/90 shadow-[0_40px_80px_-35px_rgba(15,23,42,0.18)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_45px_90px_-40px_rgba(15,23,42,0.8)]",
    accentClass:
      "border border-emerald-200/80 bg-emerald-100 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100",
    eyebrowIcon: <Lightbulb className="h-4 w-4" />,
    palette: {
      headingClass: "text-slate-900 dark:text-white",
      descriptionClass: "text-slate-600 dark:text-slate-200",
      supportingCardClass:
        "border-slate-200/70 bg-white text-slate-900 shadow-[0_24px_50px_-30px_rgba(15,23,42,0.25)] dark:border-white/12 dark:bg-slate-900/75 dark:text-white dark:shadow-[0_30px_70px_-35px_rgba(16,185,129,0.28)]",
      supportingBodyClass: "text-slate-700 dark:text-slate-100",
      supportingMetaClass: "text-slate-500 dark:text-slate-300",
      buttonVariant: "default",
      buttonClassName:
        "mt-6 w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-500 focus-visible:ring-emerald-300 dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:focus-visible:ring-emerald-300 shadow-lg shadow-emerald-200/40 dark:shadow-emerald-900/45",
    },
    decorative: (
      <>
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_55%)] dark:bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.25),rgba(15,23,42,0)_60%)]"
        />
      </>
    ),
  },
};

const CallToAction: React.FC<CallToActionProps> = ({
  variant = "aurora",
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = variantConfigs[variant];

  const handleNavigate = (): void => {
    if (onNavigate) {
      onNavigate();
      return;
    }

    navigate("/login");
  };

  const renderMarkdownBold = (text: string): ReactNode => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return (
          <strong
            key={index}
            className="text-emerald-600 dark:text-emerald-400"
          >
            {boldText}
          </strong>
        );
      }
      return part;
    });
  };

  return (
    <section
      id="cta"
      className={`py-8 px-3 sm:py-16 sm:px-6 md:py-24 transition-colors duration-500 ${config.sectionClass}`}
    >
      <div className="mx-auto max-w-6xl">
        <div
          className={`relative isolate overflow-hidden rounded-[32px] ${config.panelClass}`}
        >
          {config.decorative}
          <div className="relative z-10 flex flex-col gap-8 px-4 py-8 sm:gap-12 sm:px-6 sm:py-14 md:flex-row md:items-center md:justify-between md:px-16 md:py-20">
            <div className="max-w-xl space-y-6 text-left">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                {config.eyebrowIcon}
                <span>{t('cta.eyebrow')}</span>
              </div>
              <h2
                className={`text-balance text-4xl font-semibold tracking-tight md:text-5xl ${config.palette.headingClass}`}
              >
                <span className="block">{t('cta.title.line1')}</span>
                <span className="block">{t('cta.title.line2')}</span>
              </h2>
              <p
                className={`text-base leading-relaxed sm:text-lg md:text-xl ${config.palette.descriptionClass}`}
              >
                {renderMarkdownBold(t('cta.description'))}
              </p>

              {/* Mobile: Inline supporting content */}
              <div className="space-y-6 md:hidden">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">
                    {t('cta.cardTitle')}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-slate-700 dark:text-slate-100">
                    {renderMarkdownBold(t('cta.highlight'))}
                  </p>
                </div>
                <Button
                  onClick={handleNavigate}
                  variant={config.palette.buttonVariant}
                  className="mt-6 w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-500 focus-visible:ring-emerald-300 dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:focus-visible:ring-emerald-300 shadow-lg shadow-emerald-200/40 dark:shadow-emerald-900/45"
                >
                  <Gift className="h-5 w-5" />
                  {t('cta.ctaLabel')}
                </Button>
                <p className="text-xs text-slate-500 dark:text-slate-300">
                  {t('cta.secondary')}
                </p>
              </div>
            </div>

            {/* Desktop: Supporting card container */}
            <div
              className={`hidden w-full max-w-sm rounded-3xl border p-7 sm:p-8 text-left transition-shadow duration-300 md:block ${config.palette.supportingCardClass}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">
                {t('cta.cardTitle')}
              </p>
              <p
                className={`mt-4 text-base leading-relaxed sm:text-lg md:text-xl ${config.palette.supportingBodyClass}`}
              >
                {renderMarkdownBold(t('cta.highlight'))}
              </p>
              <Button
                onClick={handleNavigate}
                variant={config.palette.buttonVariant}
                className={config.palette.buttonClassName}
              >
                <Gift className="h-5 w-5" />
                {t('cta.ctaLabel')}
              </Button>
              <p className={`mt-4 text-xs ${config.palette.supportingMetaClass}`}>
                {t('cta.secondary')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export type { CallToActionProps, CallToActionVariant };
export default CallToAction;
